import React from 'react';
import {
    CAR_EFFECTIVENESS_METHODS,
    CAR_ROW_STATUS_OPTIONS,
    computeCarDisplayStatus,
    formatDateOnly,
} from './carReportUtils';
import { ReportPhotoGrid } from './qValueReportMedia';

const BLUE = '#1e3a5f';
const headStyle = { backgroundColor: BLUE, color: '#fff', fontWeight: 700 };
const sectionTitleStyle = {
    color: BLUE,
    fontWeight: 700,
    fontSize: '12px',
    margin: '0 0 6px',
    textTransform: 'uppercase',
};
const instructStyle = {
    fontSize: '10px',
    color: '#495057',
    marginBottom: '6px',
    lineHeight: 1.35,
};

function displayDate(value) {
    const d = formatDateOnly(value);
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
}

function padRows(rows, minCount, emptyFactory) {
    const list = [...(rows || [])];
    while (list.length < minCount) {
        list.push(emptyFactory());
    }
    return list;
}

function filterTableRows(rows, keys) {
    return (rows || []).filter((row) => keys.some((k) => String(row[k] || '').trim()));
}

function PrintPage({ children, pageIndex = 0 }) {
    const breakBefore = pageIndex > 0
        ? { pageBreakBefore: 'always', breakBefore: 'page' }
        : {};
    return (
        <div className="car-print-page" style={breakBefore}>
            {children}
        </div>
    );
}

function SectionTitle({ children }) {
    return <div style={sectionTitleStyle}>{children}</div>;
}

function Instruction({ children }) {
    return <div style={instructStyle}>{children}</div>;
}

function LabelValueGrid({ rows }) {
    return (
        <table className="car-print-grid" style={{ width: '100%', marginBottom: '10px' }}>
            <tbody>
                {rows.map(({ label, value }) => (
                    <tr key={label}>
                        <td style={{ width: '38%', fontWeight: 600, backgroundColor: '#f8f9fa' }}>{label}</td>
                        <td style={{ whiteSpace: 'pre-wrap', minHeight: '22px' }}>{value || '\u00a0'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function DataTable({ columns, rows, minRows = 2 }) {
    const padded = padRows(rows, minRows, () => ({}));
    return (
        <table className="car-print-grid" style={{ width: '100%', marginBottom: '10px' }}>
            <thead>
                <tr>
                    {columns.map((col) => (
                        <th key={col.key} style={{ ...headStyle, fontSize: '10px', padding: '5px 6px' }}>
                            {col.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {padded.map((row, i) => (
                    <tr key={`row-${i}`}>
                        {columns.map((col) => (
                            <td
                                key={col.key}
                                style={{
                                    verticalAlign: 'top',
                                    whiteSpace: 'pre-wrap',
                                    minHeight: col.minHeight || '28px',
                                    width: col.width,
                                    fontSize: '11px',
                                    padding: '6px',
                                }}
                            >
                                {col.render ? col.render(row) : (row[col.key] || '\u00a0')}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function CarReportPrintView({ data }) {
    if (!data) return null;

    const {
        carNo,
        originSource,
        dateOpened,
        partNo,
        partDescription,
        jobLotBatch,
        productionLineArea,
        ownerName,
        problemDescription,
        problemPhotos,
        containmentActions,
        rootCauseAnalysis,
        correctiveActionPlan,
        effectivenessVerification,
        status,
        dateClosed,
        targetDate,
        displayStatus: displayStatusProp,
    } = data;

    const displayStatus = displayStatusProp || computeCarDisplayStatus(status, dateClosed, targetDate);
    const rca = rootCauseAnalysis || {};
    const eff = effectivenessVerification || {};
    const selectedMethods = eff.verificationMethods || [];

    const containment = filterTableRows(containmentActions, ['actionTaken', 'responsiblePerson', 'dateCompleted', 'status']);
    const plan = filterTableRows(correctiveActionPlan, [
        'actionDescription', 'systemDocumentUpdated', 'owner', 'targetDate', 'completionDate',
    ]);

    const rcaRows = [
        { label: 'Problem Statement', value: rca.problemStatement },
        { label: 'Why? (1)', value: rca.why1 },
        { label: 'Why? (2)', value: rca.why2 },
        { label: 'Why? (3)', value: rca.why3 },
        { label: 'Why? (4)', value: rca.why4 },
        { label: 'Why? (5)', value: rca.why5 },
        { label: 'Final Root Cause Statement', value: rca.finalStatement, tall: true },
    ];

    return (
        <div className="car-report-print" style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#212529' }}>
            <style>
                {`
                .car-report-print .car-print-grid { border-collapse: collapse; }
                .car-report-print .car-print-grid th,
                .car-report-print .car-print-grid td { border: 1px solid #6c757d; padding: 5px 7px; }
                .car-report-print .car-print-title {
                    text-align: center;
                    font-size: 16px;
                    font-weight: 700;
                    letter-spacing: 0.4px;
                    margin: 0 0 14px;
                    color: #212529;
                }
                .car-report-print img { max-width: 240px; max-height: 180px; object-fit: contain; }
                @media print {
                    .car-report-print { padding: 0; }
                    .car-report-print img { page-break-inside: avoid; }
                }
                `}
            </style>

            {/* Page 1 — General, Problem, Containment (matches template column 1) */}
            <PrintPage pageIndex={0}>
                <h1 className="car-print-title">CORRECTIVE ACTION REPORT (CAR)</h1>

                <SectionTitle>1. General Information</SectionTitle>
                <LabelValueGrid
                    rows={[
                        { label: 'CAR Number', value: carNo || '' },
                        { label: 'Date Opened', value: displayDate(dateOpened) },
                        { label: 'Origin / Source', value: originSource },
                        { label: 'Part Number', value: partNo },
                        { label: 'Part Description', value: partDescription },
                        { label: 'Job / Lot / Batch Number', value: jobLotBatch },
                        { label: 'Production Line/Area', value: productionLineArea },
                        { label: 'Assigned Owner', value: ownerName },
                    ]}
                />

                <SectionTitle>2. Problem Description (The Nonconformity)</SectionTitle>
                <Instruction>
                    Provide a detailed explanation of the defect, discrepancy, or process failure. Include specific data
                    (e.g., measured dimensions vs. blueprint limits, quantities affected).
                </Instruction>
                <div
                    style={{
                        border: '1px solid #6c757d',
                        minHeight: '72px',
                        padding: '8px',
                        whiteSpace: 'pre-wrap',
                        marginBottom: '8px',
                    }}
                >
                    {problemDescription || '\u00a0'}
                </div>
                {(problemPhotos || []).length > 0 && (
                    <>
                        <div style={{ fontWeight: 600, fontSize: '10px', marginBottom: '4px' }}>Photos / evidence</div>
                        <ReportPhotoGrid items={problemPhotos} />
                    </>
                )}

                <SectionTitle>3. Immediate Containment Actions (Correction)</SectionTitle>
                <Instruction>
                    What short-term actions were taken within 24 hours to isolate the defect, stop further scrap, and protect the customer?
                </Instruction>
                <DataTable
                    minRows={2}
                    rows={containment}
                    columns={[
                        { key: 'actionTaken', label: 'Action Taken', minHeight: '36px' },
                        { key: 'responsiblePerson', label: 'Responsible Person', width: '18%' },
                        { key: 'dateCompleted', label: 'Date Completed', width: '16%', render: (r) => displayDate(r.dateCompleted) },
                        { key: 'status', label: 'Status', width: '14%' },
                    ]}
                />
            </PrintPage>

            {/* Page 2 — RCA & Corrective plan */}
            <PrintPage pageIndex={1}>
                <SectionTitle>4. Root Cause Analysis (RCA)</SectionTitle>
                <Instruction>
                    State the verified reason(s) why the problem happened (Occurrence Cause) and why it was missed by inspection
                    (Detection Cause). Use the 5-Whys methodology below.
                </Instruction>
                <table className="car-print-grid" style={{ width: '100%', marginBottom: '10px' }}>
                    <tbody>
                        {rcaRows.map(({ label, value, tall }) => (
                            <tr key={label}>
                                <td style={{ width: '34%', fontWeight: 600, verticalAlign: 'top', backgroundColor: '#f8f9fa' }}>
                                    {label}
                                </td>
                                <td style={{ whiteSpace: 'pre-wrap', minHeight: tall ? '48px' : '26px', verticalAlign: 'top' }}>
                                    {value || '\u00a0'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <SectionTitle>5. Permanent Corrective Action Plan</SectionTitle>
                <Instruction>
                    What long-term changes will prevent this specific problem from ever happening again?
                </Instruction>
                <DataTable
                    minRows={2}
                    rows={plan}
                    columns={[
                        { key: 'actionDescription', label: 'Action Description', minHeight: '36px' },
                        { key: 'systemDocumentUpdated', label: 'System/Document Updated', width: '18%' },
                        { key: 'owner', label: 'Owner', width: '12%' },
                        { key: 'targetDate', label: 'Target Date', width: '13%', render: (r) => displayDate(r.targetDate) },
                        { key: 'completionDate', label: 'Completion Date', width: '13%', render: (r) => displayDate(r.completionDate) },
                    ]}
                />
            </PrintPage>

            {/* Page 3 — Effectiveness & approval */}
            <PrintPage pageIndex={2}>
                <SectionTitle>6. Effectiveness Verification</SectionTitle>
                <Instruction>
                    To be filled out after a designated observation period (typically 30 to 90 days) to prove the fix worked.
                </Instruction>
                <table className="car-print-grid" style={{ width: '100%', marginBottom: '10px' }}>
                    <thead>
                        <tr>
                            <th style={{ ...headStyle, width: '28%', fontSize: '10px' }}>Verification Method Used</th>
                            <th style={{ ...headStyle, fontSize: '10px' }}>Evidence Checked</th>
                            <th style={{ ...headStyle, width: '16%', fontSize: '10px' }}>Verified By</th>
                            <th style={{ ...headStyle, width: '14%', fontSize: '10px' }}>Verification Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ verticalAlign: 'top', fontSize: '10px' }}>
                                {CAR_EFFECTIVENESS_METHODS.map((method) => (
                                    <div key={method} style={{ marginBottom: '4px' }}>
                                        {selectedMethods.includes(method) ? '☑' : '☐'} {method}
                                    </div>
                                ))}
                            </td>
                            <td style={{ whiteSpace: 'pre-wrap', verticalAlign: 'top', minHeight: '64px' }}>
                                {eff.evidenceChecked || '\u00a0'}
                                {(eff.evidencePhotos || []).length > 0 && (
                                    <ReportPhotoGrid items={eff.evidencePhotos} className="car-evidence-photos" />
                                )}
                            </td>
                            <td style={{ verticalAlign: 'top' }}>{eff.verifiedBy || '\u00a0'}</td>
                            <td style={{ verticalAlign: 'top' }}>{displayDate(eff.verificationDate) || '\u00a0'}</td>
                        </tr>
                    </tbody>
                </table>

                <SectionTitle>7. Submit for Approval</SectionTitle>
                <table className="car-print-grid" style={{ width: '100%', marginBottom: '6px' }}>
                    <thead>
                        <tr>
                            <th style={{ ...headStyle, fontSize: '10px' }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {CAR_ROW_STATUS_OPTIONS.map((opt) => (
                                        <div key={opt} style={{ fontSize: '11px' }}>
                                            {displayStatus === opt ? '☑' : '☐'} {opt}
                                        </div>
                                    ))}
                                </div>
                                {dateClosed && displayStatus === 'Closed' && (
                                    <div style={{ marginTop: '10px', fontSize: '10px', color: '#495057' }}>
                                        Date Closed: {displayDate(dateClosed)}
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <Instruction>
                    Select status and submit for approval. Status can be re-select each time reviewing the report.
                    User can update the progress status after review.
                </Instruction>
            </PrintPage>
        </div>
    );
}
